/**
 * Deemed Cancellation tab — CRM Cancellation Phase 4 (monitoring view + CRM-Head actions).
 * Self-contained: loads from the backend /deemedCancellation/list and hosts the Extension /
 * Confirm-Cancellation modals. Rendered by AllAllotments when the "Deemed" sub-tab is active.
 * Card markup mirrors the sibling "Cancellation" sub-tab cards in this same file exactly
 * (Card/eventWrap/cardHeader/tokenNumWrap + LeadStatusChip/Dot/ThreeDotsMenu/ReactTimeAgo).
 */
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Card, Grid, Box, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Typography } from "@mui/material";
import ReactTimeAgo from "react-time-ago";
import {
  getDeemedList, grantExtension, markReleased, uploadExtensionAttachment,
} from "../../../Services/DeemedCancellation";
import { errorTost, successTost } from "../../../Services/Toast";
import Button from "../../../Subcomponents/Button";
import ThreeDotsMenu from "../../../Subcomponents/ThreeDotsMenu";
import LeadStatusChip from "../../../Subcomponents/LeadStatusChip";
import Dot from "../../../Components/Icons/Dot";
import cssClasses from "../AllAllotments.module.css";
import EmptyListIcon from "../../../Assets/Images/Common/NoSiteVisitsFound.svg";
import CancelUnitConfirmation from "../../../Modals/CancelUnitConfirmation/CancelUnitConfirmation";
import { supabase } from "../../../supabaseClient";
import { updateLeadAllStatus, addLeadLogs, addLeadNotificationForCp } from "../../../Services/Lead";
import { updateAllotmentPayment, insertInventoryLogs } from "../../../Services/AllAllotments";
import { updateInventorStatusWiseForCancelUnit } from "../../../Services/Event";
import { refundTokenPayment } from "../../../Services/PaymentApi";
import { cancelAllotedUnit } from "../../../Services/AlotUnit";

const DAY_MS = 24 * 60 * 60 * 1000;
const fmtDate = (ms) => (ms ? new Date(ms).toLocaleDateString() : "—");
// Release day is per-project configurable (reminder_master "deemed_release" row) — derive the
// actual number from the record's own dates instead of assuming a fixed default.
const releaseDayOf = (c) => (
  c.targetReleaseDate && c.crmTaggedDate
    ? Math.round((c.targetReleaseDate - c.crmTaggedDate) / DAY_MS)
    : null
);
const chipStyle = (tone) => ({
  success: { color: "#027A48", backgroundColor: "#ECFDF3" },
  pending: { color: "#475467", backgroundColor: "#F2F4F7" },
  error: { color: "#B42318", backgroundColor: "#FEF3F2" },
  info: { color: "#175CD3", backgroundColor: "#EFF8FF" },
}[tone]);
const statusTone = (v) => (["Complete", "Paid", "Submitted"].includes(v) ? "success" : "pending");
const timelineTone = (status) => {
  const s = (status || "").toLowerCase();
  if (s.includes("reactivated")) return "success";
  if (s.includes("released") || s.includes("failed") || s.includes("confirmed")) return "error";
  if (s.includes("notice") || s.includes("extension")) return "info";
  return "pending";
};

// Shapes a Deemed-tab card into the flattened lowercase object CancelUnitConfirmation.jsx
// (genuine cancellation's charge-calc/waive-off/refund modal) expects. No CancelUnitRequests
// row exists for deemed cancellation, so cancelledinfarvision/cancelUnitRequests are always
// the "no farvision-raised request" defaults.
const buildCancelUnitData = (c) => ({
  allotmentpaymentid: c.allotmentPaymentId,
  leadid: c.leadId,
  unitid: c.unitId,
  paymentid: c.paymentId,
  cpid: c.cpId,
  unitcost: c.unitCost || { bsp: 0, gst: 0, sdr: 0, total: 0 },
  status: c.allotmentStatus,
  pricingtype: c.pricingType,
  farvisionstatus: c.farvisionStatus,
  paidamount: c.paidAmount,
  created_at: c.allotmentCreatedAt,
  cancelledinfarvision: false,
  cancelUnitRequests: [],
  leadname: c.customerName,
  projectname: c.projectName,
  wingname: c.wingName,
  unitno: c.unitNo,
});

const labelSx = { fontFamily: "Inter-Medium", fontWeight: 500, fontSize: "1.4rem", color: "#344054", marginBottom: "0.6rem", display: "block" };
const inputSx = {
  "& .MuiInputBase-root": { backgroundColor: "#F2F4F7", borderRadius: "0.8rem", fontSize: "1.4rem", fontFamily: "Inter-Regular" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#EAECF0" },
  "& .MuiInputBase-input": { padding: "1rem 1.2rem", fontSize: "1.4rem", fontFamily: "Inter-Regular", color: "#344054" },
};

export default function DeemedTab({ onCountChange }) {
  const loggedInUser = useSelector((state) => state?.Auth?.user) || {};
  // ⚠️ Permission key — confirm/configure on your side. Falls back to super-admin (level 1).
  const canAct =
    loggedInUser?.roleId?.accessibleTabs?.["All Allotments"]?.["Confirm Cancellation"]?.isAccessible ||
    loggedInUser?.roleId?.level === 1;

  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null); // card the menu/modal acts on
  const [extOpen, setExtOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // extension form
  const [extDate, setExtDate] = useState("");
  const [extReason, setExtReason] = useState("");
  const [extFile, setExtFile] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setCards(await getDeemedList(false)); }
    catch (e) { console.error("Deemed list load failed:", e); errorTost("Failed to load deemed cancellations."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Reports the "Legal Notice already sent, not yet auto-released" count up to AllAllotments.jsx
  // (status is set to "Reminding" exactly when a Legal Notice lands — original or extension-
  // resumed — and moves off it on release/reactivation/extension-pause; see the cron).
  useEffect(() => {
    onCountChange?.(cards.filter((c) => c.status === "Reminding").length);
  }, [cards, onCountChange]);

  const submitExtension = async () => {
    if (!extDate || !extReason || !extFile) { errorTost("Target date, reason and attachment are all required."); return; }
    setBusy(true);
    try {
      const url = await uploadExtensionAttachment(active.deemedCancellationId, extFile);
      await grantExtension({
        deemedCancellationId: active.deemedCancellationId,
        extensionTargetDate: new Date(extDate).getTime(),
        extensionReason: extReason,
        extensionAttachmentUrl: url,
        grantedByUserId: loggedInUser?.userId,
      });
      setExtOpen(false); setExtDate(""); setExtReason(""); setExtFile(null);
      successTost("Extension granted.");
      await load();
    } catch (e) { console.error(e); errorTost("Failed to grant extension."); }
    finally { setBusy(false); }
  };

  // Runs when CancelUnitConfirmation's Accept button is confirmed. Mirrors AllAllotments.jsx's
  // cancelUnitFlow() core writes for a single (non-merged) unit, keyed directly off the deemed
  // record's own allotment/lead/unit ids — no CancelUnitRequests row is created or touched.
  const confirmCancelUnitReturnRes = async (values, buttonValue, waiveOff, cancellationChargeValue, payload) => {
    const target = active;
    const data = buildCancelUnitData(target);
    setBusy(true);
    try {
      const { data: lsRow } = await supabase.from("LeadStatus").select("status").eq("leadId", data.leadid).maybeSingle();
      const oldLeadStatus = lsRow?.status || null;

      await updateLeadAllStatus(data.leadid, "Cancellation Done");

      if (data.pricingtype === "event" && data.paymentid) {
        const { data: statusResp } = await supabase.from("EventTokenLeadRelations").select("*").eq("paymentId", data.paymentid);
        if (statusResp?.[0]?.status === "alloted") {
          await supabase.from("EventTokenLeadRelations").update({ status: "active" }).eq("paymentId", data.paymentid);
        }
      }
      if (data.pricingtype === "preselect" && data.paymentid) {
        await supabase.from("EventTokenLeadRelations").update({ status: "cancelled" }).eq("paymentId", data.paymentid);
      }

      if (data.unitid) {
        await updateInventorStatusWiseForCancelUnit(data.unitid, data);
        await updateAllotmentPayment({ status: "Cancelled", last_updated_at: Date.now() }, data.allotmentpaymentid);
        await insertInventoryLogs({ userId: loggedInUser?.userId, leadId: data.leadid, status: "Available", unitId: data.unitid });

        await addLeadLogs({
          allotmentPaymentId: data.allotmentpaymentid,
          leadId: data.leadid,
          leadStatus: "Cancellation Done",
          message: `Cancellation Done for ${data.wingname}-${data.unitno}, ${data.projectname} (Deemed Cancellation confirmed by ${loggedInUser?.name}). ${buttonValue === "acceptWithoutRefund" ? "Hold Refund, " : ""}Cancellation Charges ₹${cancellationChargeValue ?? 0}, Waived charges ₹${payload?.waivedOffCancellationCharges ?? 0}, Total Cancellation Charges ₹${payload?.newCancellationCharges ?? 0}${waiveOff ? ", WaivedOff Reason:" + payload?.waivedOffReason : ""}.`,
        });

        if (data.cpid) {
          await addLeadNotificationForCp({
            action: `Cancellation has been done for Unit ${data.unitno} in wing ${data.wingname} of ${data.projectname} by ${loggedInUser?.name}(${loggedInUser?.roleId?.title}) for ${data.leadname} — Deemed Cancellation`,
            cpId: data.cpid, leadId: data.leadid, userId: loggedInUser?.userId, allotmentPaymentId: data.allotmentpaymentid, eventId: null,
          });
        }

        const refund = buttonValue === "acceptWithRefund";
        const refundBody = { mode: "Allotment", allotmentPaymentId: data.allotmentpaymentid, cancellationCharges: values?.cancellationCharges, refund };
        const respRefundTokenPay = await refundTokenPayment(refundBody);
        if (respRefundTokenPay?.data?.message?.includes("Insufficient balance")) {
          errorTost("Cancellation done successfully! Your account does not have sufficient balance to carry out the refund operation");
        } else if (respRefundTokenPay?.data?.success?.[0]?.status === "success") {
          successTost(refund ? "Cancellation done successfully and Allotment refund initiated successfully" : "Cancellation done successfully!");
        } else if (respRefundTokenPay?.data?.failed?.[0]) {
          errorTost("Cancellation done successfully but error while refunding. " + respRefundTokenPay?.data?.failed[0]?.reason);
        } else {
          successTost("Cancellation done successfully!");
        }
      }

      // Audit-trail row only — mirrors the CancelUnitRequests entry genuine cancellation
      // leaves behind, but nothing above reads it back; the release itself already ran.
      await cancelAllotedUnit(
        loggedInUser?.userId,
        data.allotmentpaymentid,
        `Deemed Cancellation${waiveOff ? " (waived off)" : ""}`,
        "Accepted",
        data.unitid,
        { parentId: null, roleId: null },
        data.leadid,
        "Accepted",
        oldLeadStatus,
        cancellationChargeValue,
        waiveOff,
        {
          waivedOffReason: payload?.waivedOffReason,
          waivedOffCancellationCharges: payload?.waivedOffCancellationCharges,
          newCancellationCharges: payload?.newCancellationCharges,
        },
        target.deemedCancellationId
      ).catch((e) => console.error("Deemed cancellation: CancelUnitRequests audit insert failed:", e));

      await markReleased({
        deemedCancellationId: target.deemedCancellationId,
        confirmReason: `Deemed Cancellation confirmed with charges ₹${cancellationChargeValue ?? 0}${waiveOff ? " (waived off)" : ""}`,
        confirmedByUserId: loggedInUser?.userId,
        cancellationCharges: values?.cancellationCharges,
        unitCost: target.crmOldUnitCost,
        isWaiveOff: waiveOff,
        newCancellationCharges: payload?.newCancellationCharges,
        waiveOffCharges: payload?.waivedOffCancellationCharges,
        waiveOffReason: payload?.waivedOffReason,
      });

      setConfirmOpen(false);
      await load();
    } catch (e) {
      console.error(e);
      errorTost("Error while confirming cancellation.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ height: "100%", overflow: "auto" }}>
      {!loading && cards.length === 0 ? (
        <div className={cssClasses.noDataWrapper}>
          <div className={cssClasses.noDataMsg}>
            <div className={cssClasses.emptyIcon}>
              <img src={EmptyListIcon} alt="No deemed cancellations" />
            </div>
            <div className={cssClasses.emptyTitle}>No allotments in deemed cancellation.</div>
          </div>
        </div>
      ) : (
        <Grid
          style={{
            marginTop: "1rem",
            marginBottom: "1rem",
            marginLeft: "1rem",
            marginRight: "1rem",
            gap: "2rem",
            width: "calc(100% - 2rem)",
          }}
          container
          spacing={{ xs: 2, md: 3, lg: 3 }}
          columns={{ xs: 4, sm: 4, md: 8, lg: 8 }}
        >
          {cards.map((c) => {
            const releaseDay = releaseDayOf(c);
            return (
              <Card className={cssClasses.eventWrap} key={c.deemedCancellationId}>
                <div className={cssClasses.cardHeader}>
                  <div className={cssClasses.tokenNumWrap}>
                    {c.wingName && <span>{c.wingName} - {c.unitNo}</span>}
                    <span>|</span>
                    {c.projectName && <span>{c.projectName}</span>}
                    <div>
                      <LeadStatusChip
                        icon={<Dot width={"0.5rem"} height={"0.5rem"} />}
                        style={chipStyle(c.extensionActive ? "info" : "error")}
                        label={c.extensionActive ? "Extended" : (c.dayCount != null ? `Day ${c.dayCount}${releaseDay != null ? ` of ${releaseDay}` : ""}` : c.status)}
                      />
                    </div>
                    <div>
                      <LeadStatusChip icon={<Dot width={"0.5rem"} height={"0.5rem"} />} style={chipStyle(statusTone(c.registrationStatus))} label={`Registration: ${c.registrationStatus}`} />
                    </div>
                    <div>
                      <LeadStatusChip icon={<Dot width={"0.5rem"} height={"0.5rem"} />} style={chipStyle(statusTone(c.sdrStatus))} label={`SDR: ${c.sdrStatus}`} />
                    </div>
                    <div>
                      <LeadStatusChip icon={<Dot width={"0.5rem"} height={"0.5rem"} />} style={chipStyle(statusTone(c.agreementKycStatus))} label={`Agreement KYC: ${c.agreementKycStatus}`} />
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                    alignItems: "center",
                  }}
                >
                  <div className={cssClasses.tokenStatus}>
                    <LeadStatusChip
                      icon={<Dot width={"0.5rem"} height={"0.5rem"} />}
                      style={{ color: "#344054", backgroundColor: "#F2F4F7" }}
                      label={c.customerName || "—"}
                    />
                  </div>
                  <div className={cssClasses.tokenLastUpdate}>
                    <ReactTimeAgo style={{ fontSize: "1.2rem" }} date={c.dcTriggeredDate || c.crmTaggedDate} locale="en-US" />
                  </div>
                  {canAct && (
                    <div className={cssClasses.tokenActions}>
                      <ThreeDotsMenu
                        list={["Timeline", "Extension", "Confirm Cancellation"]}
                        selectedValue={(e) => {
                          setActive(c);
                          if (e === "Timeline") setTimelineOpen(true);
                          else if (e === "Extension") setExtOpen(true);
                          else if (e === "Confirm Cancellation") {
                            if (!c.leadId || !c.unitId || !c.allotmentPaymentId) {
                              console.error("Deemed cancellation: card is missing leadId/unitId/allotmentPaymentId — /list response:", c);
                              errorTost("This record is missing lead/unit data (backend may need a restart to pick up the updated /list response) — cannot open Confirm Cancellation.");
                              return;
                            }
                            setConfirmOpen(true);
                          }
                        }}
                      />
                    </div>
                  )}
                </div>

                <div className={cssClasses.cardFooter} style={{ fontSize: "1.2rem", color: "#667085" }}>
                  <span>RM: {c.assignedRm || "—"}</span>
                  <span>CRM tagged: {fmtDate(c.crmTaggedDate)}</span>
                  <span>Target release: {fmtDate(c.extensionTargetDate || c.targetReleaseDate)}</span>
                </div>
              </Card>
            );
          })}
        </Grid>
      )}

      {/* Extension modal */}
      <Dialog open={extOpen} onClose={() => !busy && setExtOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontFamily: "Inter-Medium", color: "#344054" }}>Grant Extension</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <Box>
            <Typography sx={labelSx}>New target date</Typography>
            <TextField fullWidth size="small" type="date" InputLabelProps={{ shrink: true }} value={extDate} onChange={(e) => setExtDate(e.target.value)} required sx={inputSx} />
          </Box>
          <Box>
            <Typography sx={labelSx}>Reason</Typography>
            <TextField fullWidth size="small" multiline minRows={2} value={extReason} onChange={(e) => setExtReason(e.target.value)} required sx={inputSx} />
          </Box>
          <Button
            style={{
              fontSize: "1.4rem", color: "#344054", backgroundColor: "#FFFFFF",
              border: "1px solid #D0D5DD", borderRadius: "0.8rem", padding: "0.8rem 1.6rem",
            }}
            btnName={extFile ? extFile.name : "Attach document (required)"}
            clicked={() => document.getElementById("deemed-ext-file-input")?.click()}
          />
          <input id="deemed-ext-file-input" hidden type="file" onChange={(e) => setExtFile(e.target.files?.[0] || null)} />
        </DialogContent>
        <DialogActions sx={{ padding: "1.6rem 2.4rem" }}>
          <Button
            style={{ fontSize: "1.4rem", color: "#344054", backgroundColor: "#FFFFFF", border: "1px solid #D0D5DD", borderRadius: "0.8rem", padding: "0.8rem 2rem" }}
            btnName="Cancel"
            clicked={() => !busy && setExtOpen(false)}
            disable={busy}
          />
          <Button
            style={{ fontSize: "1.4rem", color: "#FFF", backgroundColor: busy ? "#84ADFF" : "#155EEF", borderRadius: "0.8rem", padding: "0.8rem 2rem" }}
            btnName={busy ? "Saving…" : "Grant Extension"}
            clicked={submitExtension}
            disable={busy}
          />
        </DialogActions>
      </Dialog>

      {/* Timeline — latest LeadLogs for this allotment (Triggered, Legal Notice, reminders,
          extension, release, etc.), newest first. Opened from the three-dots menu. */}
      <Dialog open={timelineOpen} onClose={() => setTimelineOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontFamily: "Inter-Medium", color: "#344054" }}>Timeline</DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 3 }}>
          {active?.timeline?.length > 0 ? (
            <div style={{ position: "relative", paddingLeft: "2rem" }}>
              <div style={{
                position: "absolute", left: "0.5rem", top: "0.3rem", bottom: "0.3rem",
                width: "2px", backgroundColor: "#EAECF0",
              }} />
              {active.timeline.map((t, i) => {
                const tone = chipStyle(timelineTone(t.status));
                return (
                  <div key={i} style={{ position: "relative", paddingBottom: i === active.timeline.length - 1 ? 0 : "1.6rem" }}>
                    <span style={{
                      position: "absolute", left: "-2rem", top: "0.1rem",
                      width: "1.1rem", height: "1.1rem", borderRadius: "50%",
                      backgroundColor: "#FFFFFF", border: `2px solid ${tone.color}`, boxSizing: "border-box",
                    }} />
                    <div style={{
                      display: "inline-block", fontSize: "1.1rem", fontFamily: "Inter-Medium",
                      color: tone.color, backgroundColor: tone.backgroundColor,
                      borderRadius: "0.6rem", padding: "0.2rem 0.8rem", marginBottom: "0.4rem",
                    }}>
                      {t.status || "Update"}
                    </div>
                    <div style={{ fontSize: "1.2rem", color: "#475467", lineHeight: 1.5 }}>{t.message || "—"}</div>
                    {t.created_at && (
                      <div style={{ marginTop: "0.2rem" }}>
                        <ReactTimeAgo style={{ fontSize: "1.1rem", color: "#98A2B3" }} date={t.created_at} locale="en-US" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <Typography sx={{ fontSize: "1.3rem", color: "#667085" }}>No activity yet.</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "1.6rem 2.4rem" }}>
          <Button
            style={{ fontSize: "1.4rem", color: "#344054", backgroundColor: "#FFFFFF", border: "1px solid #D0D5DD", borderRadius: "0.8rem", padding: "0.8rem 2rem" }}
            btnName="Close"
            clicked={() => setTimelineOpen(false)}
          />
        </DialogActions>
      </Dialog>

      {/* Confirm Cancellation — reuses genuine cancellation's own charge-calc/waive-off/refund
          modal, wired to confirmCancelUnitReturnRes() instead of the CancelUnitRequests-based
          accept flow AllAllotments.jsx uses (deemed cancellation never creates that row). */}
      {confirmOpen && active && (
        <CancelUnitConfirmation
          open={confirmOpen}
          handleClose={() => !busy && setConfirmOpen(false)}
          data={buildCancelUnitData(active)}
          cancellationRequest={buildCancelUnitData(active)}
          returnRes={confirmCancelUnitReturnRes}
        />
      )}
    </Box>
  );
}
